import { BarChart } from '@mantine/charts';
import { Button } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { CountByDayByForm, FormSummary } from '@/api';
import { DataTable } from '@/components/Ui/DataTable';
import { EmptyText } from '@/components/Ui/EmptyText';
import { Page } from '@/components/Ui/Page';
import { PageHeader } from '@/components/Ui/PageHeader';
import { Section, SectionBody } from '@/components/Ui/Section';
import { useLanguageAwareRouting } from '@/hooks/useLanguageAwareRouting';
import { integerTicks } from '@/lib/chart-ticks';
import { formatNumber, formatPercent } from '@/lib/money';
import { shortDate } from '@/lib/relative-time';
import { useRustyState } from '@/state';
import { chartOther, chartSeries } from '@/theme';
import classes from './Volume.module.css';
import { isScored, spamShare } from './volume';

const DAYS = 30;
const SPAM_COLOR = '#6250d6';
const OTHER = 'other';

type Load =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; rows: CountByDayByForm[] };

interface FormRow {
  key: string;
  name: string;
  color: string;
  total: number;
  spam: number;
  /** Messages of the row's forms that were scored; the share divides by this. */
  scoredTotal: number;
  /** False only when every form in the row has the spam filter off. */
  scored: boolean;
}

/** The last DAYS UTC dates, oldest first, as the backend's `YYYY-MM-DD`. */
function dayKeys(): string[] {
  const today = Date.UTC(
    new Date().getUTCFullYear(),
    new Date().getUTCMonth(),
    new Date().getUTCDate()
  );
  return Array.from({ length: DAYS }, (_, i) =>
    new Date(today - (DAYS - 1 - i) * 86_400_000).toISOString().slice(0, 10)
  );
}

export function VolumeScreen() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.split('-')[0];
  const { createLanguageURL } = useLanguageAwareRouting();
  const forms = useRustyState((s) => s.formsSummary);
  const [load, setLoad] = useState<Load>({ status: 'loading' });

  useEffect(() => {
    let ignore = false;
    const { api, refreshFormsSummary } = useRustyState.getState();
    void refreshFormsSummary();
    api
      .getMessagesCountByDay({ days: DAYS })
      .then((rows) => {
        if (!ignore) setLoad({ status: 'ready', rows: Array.isArray(rows) ? rows : [] });
      })
      .catch(() => {
        if (!ignore) setLoad({ status: 'error' });
      });
    return () => {
      ignore = true;
    };
  }, []);

  const days = dayKeys();
  const fmt = (n: number) => formatNumber(n, locale);
  const pct = (part: number, whole: number) =>
    whole > 0 ? formatPercent((part / whole) * 100, locale, 1) : '–';

  const header = (
    <PageHeader
      title={t('inbox.volumeTitle')}
      breadcrumb={[{ label: t('inbox.pageTitle'), to: '/account/messages' }]}
      subtitle={t('inbox.volumeIntro', {
        from: shortDate(days[0], locale),
        to: shortDate(days[days.length - 1], locale, true),
      })}
    />
  );

  if (load.status !== 'ready') {
    return (
      <Page>
        {header}
        <EmptyText>
          {load.status === 'loading' ? t('ui.loading') : t('inbox.volumeLoadFailed')}
        </EmptyText>
      </Page>
    );
  }

  const rows = load.rows.filter((r) => days.includes(r.day));
  const byForm = new Map<string, { total: number; spam: number }>();
  for (const r of rows) {
    const acc = byForm.get(r.form_id) ?? { total: 0, spam: 0 };
    acc.total += r.total;
    acc.spam += r.spam;
    byForm.set(r.form_id, acc);
  }
  const summaryOf = (id: string): FormSummary | undefined => forms?.find((f) => f.id === id);
  const scoredIds = new Set(
    [...byForm.entries()]
      .filter(([id, v]) => isScored(v.spam, summaryOf(id)?.filter_spam))
      .map(([id]) => id)
  );
  const scored = (id: string) => scoredIds.has(id);
  const ranked = [...byForm.entries()].sort((a, b) => b[1].total - a[1].total);
  // A single leftover form keeps its name instead of becoming "1 other form".
  const shown = ranked.length === chartSeries.length + 1 ? ranked.length : chartSeries.length;
  const top = ranked.slice(0, shown);
  const rest = ranked.slice(shown);
  const seriesKey = new Map(top.map(([id], i) => [id, `f${i}`]));

  const formRows: FormRow[] = top.map(([id, v], i) => ({
    key: `f${i}`,
    name: summaryOf(id)?.title ?? t('inbox.volumeDeletedForm'),
    color: chartSeries[i] ?? chartOther,
    total: v.total,
    spam: v.spam,
    scoredTotal: scored(id) ? v.total : 0,
    scored: scored(id),
  }));
  if (rest.length > 0) {
    formRows.push({
      key: OTHER,
      name: t('inbox.volumeOther', { count: rest.length }),
      color: chartOther,
      total: rest.reduce((n, [, v]) => n + v.total, 0),
      spam: rest.reduce((n, [, v]) => n + v.spam, 0),
      scoredTotal: rest.reduce((n, [id, v]) => n + (scored(id) ? v.total : 0), 0),
      scored: rest.some(([id]) => scored(id)),
    });
  }

  const chartData = days.map((day) => {
    const point: Record<string, string | number> = { day: shortDate(day, locale) };
    for (const r of formRows) point[r.key] = 0;
    let total = 0;
    const dayRows = rows.filter((r) => r.day === day);
    for (const r of dayRows) {
      const key = seriesKey.get(r.form_id) ?? OTHER;
      point[key] = (point[key] as number) + r.total;
      total += r.total;
    }
    point.total = total;
    const { share } = spamShare(dayRows, scored);
    point.share = share === null ? 0 : Math.round(share * 1000) / 10;
    return point;
  });

  const allTotal = formRows.reduce((n, r) => n + r.total, 0);
  const all = spamShare(rows, scored);
  const maxShare = Math.max(0, ...chartData.map((p) => p.share as number));
  const volumeTicks = integerTicks(Math.max(0, ...chartData.map((p) => p.total as number)));
  // The share chart is 110px high: three labels fit.
  const shareTicks = integerTicks(maxShare, 2);

  return (
    <Page>
      {header}

      {allTotal === 0 ? (
        <EmptyText>{t('inbox.volumeEmpty')}</EmptyText>
      ) : (
        <>
          <Section title={t('inbox.volumeByDay')}>
            <SectionBody>
              <div role="img" aria-label={t('inbox.volumeChartLabel', { total: fmt(allTotal) })}>
                <BarChart
                  h={240}
                  data={chartData}
                  dataKey="day"
                  type="stacked"
                  withLegend
                  legendProps={{ verticalAlign: 'top', height: 40 }}
                  gridAxis="y"
                  tickLine="none"
                  series={formRows.map((r) => ({ name: r.key, label: r.name, color: r.color }))}
                  valueFormatter={fmt}
                  yAxisProps={{
                    allowDecimals: false,
                    interval: 0,
                    ticks: volumeTicks,
                    domain: [0, volumeTicks[volumeTicks.length - 1]],
                  }}
                />
              </div>
            </SectionBody>
          </Section>

          <Section
            title={t('inbox.volumeSpamTitle')}
            subtitle={t('inbox.volumeSpamSummary', {
              spam: fmt(all.spam),
              total: fmt(all.scoredTotal),
              share: pct(all.spam, all.scoredTotal),
            })}
          >
            <SectionBody>
              <div
                role="img"
                aria-label={t('inbox.volumeSpamChartLabel', {
                  max: formatPercent(maxShare, locale, 1),
                })}
              >
                <BarChart
                  h={110}
                  data={chartData}
                  dataKey="day"
                  gridAxis="none"
                  tickLine="none"
                  withXAxis={false}
                  series={[
                    { name: 'share', label: t('inbox.volumeSpamSeries'), color: SPAM_COLOR },
                  ]}
                  valueFormatter={(v) => formatPercent(v, locale, 1)}
                  yAxisProps={{
                    allowDecimals: false,
                    interval: 0,
                    ticks: shareTicks,
                    domain: [0, shareTicks[shareTicks.length - 1]],
                    width: 44,
                  }}
                />
              </div>
            </SectionBody>
          </Section>

          <Section
            title={t('inbox.volumeByForm')}
            actions={
              <Button component={Link} to={createLanguageURL('/account/forms')} size="xs">
                {t('inbox.volumeAllForms')}
              </Button>
            }
          >
            <DataTable
              caption={t('inbox.volumeByForm')}
              rowKey={(r) => r.key}
              rows={formRows}
              columns={[
                {
                  key: 'form',
                  header: t('inbox.volumeColForm'),
                  render: (r) => (
                    <span className={classes.name}>
                      <span
                        className={classes.swatch}
                        style={{ background: r.color }}
                        aria-hidden="true"
                      />
                      {r.name}
                    </span>
                  ),
                },
                {
                  key: 'total',
                  header: t('inbox.volumeColMessages'),
                  align: 'num',
                  render: (r) => fmt(r.total),
                },
                {
                  key: 'spam',
                  header: t('inbox.volumeColSpam'),
                  align: 'num',
                  render: (r) => (r.scored ? fmt(r.spam) : t('inbox.volumeFilterOff')),
                },
                {
                  key: 'share',
                  header: t('inbox.volumeColShare'),
                  align: 'num',
                  render: (r) => (r.scored ? pct(r.spam, r.scoredTotal) : '–'),
                },
              ]}
            />
          </Section>
        </>
      )}
    </Page>
  );
}
